import { resolveId } from '@medplum/core';
import type { Observation, Reference } from '@medplum/fhirtypes';

/**
 * Walk a `DiagnosticReport.result[]` ref list plus every loaded Observation's `hasMember[]`
 * and return the set of unique Observation ids reachable so far.
 *
 * The walk only descends into Observations present in `loaded`, so the discoverable id set
 * grows monotonically across renders as more Observations resolve and the caller pushes them
 * into a cumulative state-backed map. Convergence: every loaded Observation's members are
 * themselves loaded (or have invalid/missing ids).
 *
 * Cycles short-circuit via the `seen` set: an Observation that references itself (directly
 * or transitively through `hasMember`) is visited once and its members are walked exactly
 * once, never re-entering the same id.
 *
 * Refs missing an id (`reference` strings that don't parse) are silently skipped.
 */
export function collectObservationIds(rootRefs: Reference<Observation>[], loaded: Map<string, Observation>): string[] {
  const seen = new Set<string>();
  const collect = (refs: Reference<Observation>[]): void => {
    for (const ref of refs) {
      const id = resolveId(ref);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const obs = loaded.get(id);
      if (obs?.hasMember) collect(obs.hasMember as Reference<Observation>[]);
    }
  };
  collect(rootRefs);
  return [...seen];
}
